import { curveMonotoneX } from '@visx/curve';
import { useParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { useId } from 'react';
import { cn } from '../../lib/cn.ts';
import type { DataSlot } from './PulseChart.tsx';

export interface TrendAreaProps {
  /** Oldest first. */
  values: readonly number[];
  /** Fixed scale; default 0 to the data's max. Use 0 and 1 for load fractions. */
  min?: number;
  max?: number;
  /** A chart slot (data-viz.md): never a status colour, never the accent. */
  slot?: DataSlot;
  /** Plot height in px: 40 on a card, 96 in a drawer. */
  height?: number;
  /** Values that get a dashed guide line (at most 3), for example [0.5, 1]. */
  guides?: readonly number[];
  /** No current reading (offline): the last known shape as a faint dashed line, no fill, no point. */
  muted?: boolean;
  /** Accessible summary: "CPU load, last 10 minutes, now 42%". */
  label: string;
  /** Fixed width instead of measuring the container (tests). */
  width?: number;
  className?: string;
}

/**
 * A trend that fills its column: a soft area under one line in a chart slot, dashed guides
 * instead of an axis, and the latest point marked. The line draws itself once; later samples
 * just move it. Offline, it keeps the last shape, grey and dashed, so a quiet machine reads quiet.
 */
export function TrendArea({ values, min = 0, max, slot = 1, height = 40, guides = [], muted, label, width: fixed, className }: TrendAreaProps) {
  const gid = `trend${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const { parentRef, width: measured } = useParentSize({ initialSize: { width: fixed ?? 240 }, debounceTime: 50 });
  const width = Math.max(24, fixed ?? measured);
  const colour = `var(--data-${slot})`;
  const pad = 3;
  const hi = max ?? Math.max(min + 1e-9, ...values);
  const x = scaleLinear<number>({ domain: [0, Math.max(1, values.length - 1)], range: [pad, width - pad] });
  const y = scaleLinear<number>({ domain: [min, hi === min ? min + 1 : hi], range: [height - pad, pad], clamp: true });
  const last = values.length - 1;
  return (
    <div ref={parentRef} className={cn('relative min-w-0', className)} style={{ height }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} className="absolute inset-0 overflow-visible">
        {guides.slice(0, 3).map((g) => (
          <line key={g} x1={0} x2={width} y1={y(g)} y2={y(g)} stroke="var(--border-subtle)" strokeDasharray="2 4" />
        ))}
        <line x1={0} x2={width} y1={height - 0.5} y2={height - 0.5} stroke="var(--border-subtle)" />
        {values.length < 2 ? null : muted ? (
          <LinePath<number> data={values as number[]} x={(_, i) => x(i)} y={(v) => y(v)} curve={curveMonotoneX} stroke="var(--fg-faint)" strokeWidth={1.5} strokeDasharray="3 4" strokeLinecap="round" />
        ) : (
          <>
            <defs>
              <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={colour} stopOpacity="0.32" />
                <stop offset="100%" stopColor={colour} stopOpacity="0" />
              </linearGradient>
            </defs>
            <AreaClosed<number> data={values as number[]} x={(_, i) => x(i)} y={(v) => y(v)} yScale={y} curve={curveMonotoneX} fill={`url(#${gid})`} />
            <LinePath<number>
              className="motion-draw"
              pathLength={1}
              strokeDasharray="1"
              data={values as number[]}
              x={(_, i) => x(i)}
              y={(v) => y(v)}
              curve={curveMonotoneX}
              stroke={colour}
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <circle cx={x(last)} cy={y(values[last] ?? min)} r={4.5} fill={colour} opacity={0.2} />
            <circle cx={x(last)} cy={y(values[last] ?? min)} r={2.25} fill={colour} />
          </>
        )}
      </svg>
    </div>
  );
}
